// pages/TikTokLandingPage.tsx — strona lądowania wyłącznie dla ruchu z TikToka (link w bio).
//
// Zasady, które ją kształtują (guidelines/TIKTOK-PLAYBOOK-2026.md, sekcja 4 i 6):
//  1. Jeden ekran, jedna obietnica, jedno działanie. Bez menu i bez rozpraszaczy, bo widz przyszedł
//     z konkretnego filmu i ma jeden powód, żeby tu być.
//  2. Pytanie kwalifikujące PRZED polami kontaktowymi. Po wysłaniu kontaktu nikt nie wraca do pytań.
//  3. Telefon i mail jako prawdziwe odnośniki tel:/mailto:, nie tekst do przepisania. Brak tych
//     odnośników w HTML potrafi rozsypać cały lejek i ten błąd zdarzył się już w innym projekcie.
//     Weryfikacja po wdrożeniu przez curl, nie na oko w przeglądarce.
//  4. Język właściciela małej firmy, bez żargonu. Zero słów „pipeline", „webhook", „MCP".

import React from 'react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Clock, MessageCircle, Phone, Mail } from 'lucide-react';
import { LeadForm, type LeadQualifier } from '../components/LeadForm';
import { BrandMark } from '../components/BrandMark';
import { GlassCard } from '../components/ui/GlassCard';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';

// Opcje odpowiadają lanom treści z playbooka, żeby odpowiedź od razu mówiła, z którego filmu
// przyszedł widz i czego dotyczy jego ręczna robota.
const QUALIFIER: LeadQualifier = {
    label: 'Co zjada Ci najwięcej czasu?',
    options: [
        'Przepisywanie danych między programami',
        'Odpisywanie na te same pytania klientów',
        'Faktury, dokumenty i papierologia',
        'Coś innego, powiem w wiadomości',
    ],
};

// Dane wzięte z tego repo (tel:+48791581239 i wa.me/48791581239 występują w komponentach kontaktu,
// kontakt@biznesbezklikania.pl w danych strony), a nie wymyślone. Jedno źródło = brak rozjazdu.
const CONTACT_PHONE = '+48791581239';
const CONTACT_PHONE_LABEL = '791 581 239';
const CONTACT_EMAIL = 'kontakt@biznesbezklikania.pl';

export const TikTokLandingPage: React.FC = () => {
    usePageMetadata({
        title: 'Z TikToka — pokaż, co robisz ręcznie | Biznes Bez Klikania',
        description:
            'Napisz, co w Twojej firmie robi się ręcznie. Odpiszę, czy da się to zdjąć z głowy i ile to zajmie.',
    });

    return (
        <main className="min-h-screen px-4 py-12 md:py-20">
            <div className="max-w-2xl mx-auto">
                {/* Sam znak i nazwa, bez nawigacji. Strona renderuje się bez nagłówka serwisu
                    (App.tsx, BARE_LANDING_ROUTES), więc odwiedzający musi skądś wiedzieć,
                    gdzie trafił — ale nie dostaje dwudziestu odnośników do klikania. */}
                <div className="flex items-center gap-2 mb-10">
                    <BrandMark className="w-7 h-7 shrink-0" />
                    <span className="font-bold text-lg tracking-tight">Biznes bez Klikania</span>
                </div>

                <RevealOnScroll>
                    <p className="text-sm font-semibold uppercase tracking-wider text-primary-light dark:text-primary-dark mb-3">
                        Widzimy się z TikToka
                    </p>
                    <h1 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark mb-4">
                        Pokaż mi, co robisz ręcznie. Powiem, czy da się to zdjąć z głowy.
                    </h1>
                    <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mb-8">
                        Bez prezentacji i bez wciskania. Opisujesz jedną czynność, którą klikasz w kółko,
                        a ja odpisuję, czy warto ją automatyzować i ile to zajmie. Jeśli nie warto, też to napiszę.
                    </p>
                </RevealOnScroll>

                <RevealOnScroll>
                    <GlassCard variant="strong" className="mb-8">
                        <LeadForm source="tiktok" qualifier={QUALIFIER} />
                    </GlassCard>
                </RevealOnScroll>

                <RevealOnScroll>
                    <div className="flex items-center gap-2 text-sm text-text-secondary-light dark:text-text-secondary-dark mb-8">
                        <Clock className="w-4 h-4 shrink-0" />
                        <span>Odpisuję zwykle tego samego dnia roboczego.</span>
                    </div>
                </RevealOnScroll>

                <RevealOnScroll>
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
                            Wolisz od razu napisać albo zadzwonić?
                        </p>
                        {/* Prawdziwe odnośniki, nie tekst. Na telefonie to jedno dotknięcie zamiast przepisywania. */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <a
                                href={`tel:${CONTACT_PHONE}`}
                                data-analytics-id="tiktok_landing_tel"
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border font-semibold hover:border-gray-400 transition"
                            >
                                <Phone className="w-4 h-4" /> {CONTACT_PHONE_LABEL}
                            </a>
                            <a
                                href={`https://wa.me/${CONTACT_PHONE.replace('+', '')}?text=${encodeURIComponent('Cześć, piszę z TikToka. U mnie ręcznie robi się: ')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-analytics-id="tiktok_landing_wa"
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#25D366] text-white font-semibold hover:opacity-90 transition"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </a>
                            <a
                                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Pytanie z TikToka')}`}
                                data-analytics-id="tiktok_landing_mail"
                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border font-semibold hover:border-gray-400 transition"
                            >
                                <Mail className="w-4 h-4" /> E-mail
                            </a>
                        </div>
                    </div>
                </RevealOnScroll>
            </div>
        </main>
    );
};

export default TikTokLandingPage;
